
import React, { useState, useRef } from "react";
import { Typography, Button, TextField, Stack } from "@mui/material";
import { CommonLayout, FormWrapper } from "../components/CommonLayout";
import { useSearchParams, useNavigate } from "react-router-dom";
import { validateActivationCode, validateCreateActivationCode, ActivationMode } from "../services/activation.service";
import NotificationBanner from "../components/common/NotificationBanner";


const CodeInput = ({ value, onChange, error, ...props }: any) => (
  <TextField
    value={value}
    onChange={onChange}
    inputProps={{ maxLength: 1, style: { textAlign: "center", fontSize: 24, padding: 0 } }}
    sx={{
      width: 42,
      height: 42,
      mx: 2,
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        padding: '0px !important',
        minHeight: '42px',
      },
      '& input': {
        padding: '0px !important',
        height: '42px',
        boxSizing: 'border-box',
      },
    }}
    error={error}
    {...props}
  />
);

const CodeVerifyPage: React.FC = () => {
  const [code, setCode] = useState(["", "", "", ""]);
  const [errorType, setErrorType] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, type: "info", title: "", message: "" });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = (searchParams.get("token") || "").trim();
  const mode = ((searchParams.get("mode") || "create").toLowerCase() as ActivationMode);
  const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

  const handleChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length > 1) return;
    const newCode = [...code];
    newCode[idx] = val;
    setCode(newCode);
    setErrorType("");
    if (val && idx < 3) {
      inputRefs[idx + 1].current?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const codeStr = code.join("");
    if (!token) {
      setNotification({ open: true, type: "error", title: "Token faltante", message: "El enlace no contiene un token válido." });
      return;
    }
    if (codeStr.length !== 4) return;
    setIsSubmitting(true);
    setErrorType("");
    try {
      if (mode === "create") {
        await validateCreateActivationCode(codeStr, token);
      } else {
        await validateActivationCode(codeStr, token);
      }
      setNotification({ open: true, type: "success", title: "Código verificado", message: "Verificación exitosa. Continuemos." });
      // Redirigir a crear/cambiar contraseña con el mismo token y modo
      navigate(`/create-password?mode=${mode === "reset" ? "change" : "create"}&token=${encodeURIComponent(token)}&code=${encodeURIComponent(codeStr)}`);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 410) {
        setErrorType("expired");
      } else if (status === 400 || status === 401) {
        setErrorType("invalid");
      } else {
        setNotification({ open: true, type: "error", title: "Error", message: "No se pudo validar el código. Intenta nuevamente." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    // Simulación de reenvío
    setErrorType("");
    setCode(["", "", "", ""]);
    inputRefs[0].current?.focus();
  };

  return (
    <CommonLayout>
      <FormWrapper elevation={3}>
        <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
          <Typography fontSize={21} fontWeight={700}>
            Ingresa el código
          </Typography>
          <Typography textAlign="center" color="#bdbdbd" fontWeight={400}>
            Enviamos un código de verificación de 4 dígitos a tu correo electrónico, ingrésalo para continuar.
          </Typography>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {code.map((digit, idx) => (
              <CodeInput
                key={idx}
                value={digit}
                onChange={(e: any) => handleChange(idx, e)}
                error={!!errorType}
                inputRef={inputRefs[idx]}
              />
            ))}
          </div>
          {errorType === "invalid" && (
            <Typography color="error" align="center">
              El código ingresado no es válido. Verifica los números e inténtalo nuevamente.
            </Typography>
          )}
          {errorType === "expired" && (
            <>
              <Typography color="error" align="center">
                El código ingresado ha expirado.
                <br />
                <span style={{ fontWeight: 500 }}>Solicita uno nuevo para continuar.</span>
              </Typography>
              <Button
                variant="contained"
                fullWidth
                sx={{ backgroundColor: "#002E74", color: "white", fontWeight: "bold" }}
                onClick={handleResend}
              >
                Reenviar código
              </Button>
            </>
          )}
          <Button
            variant="contained"
            fullWidth
            sx={{ backgroundColor: "#002E74", color: "white", fontWeight: "bold", fontSize: 15, py: 1.5 }}
            disabled={code.some((d) => d === "") || isSubmitting}
            onClick={handleSubmit}
          >
            Verificar y continuar
          </Button>
          <NotificationBanner
            type={notification.type as any}
            title={notification.title}
            message={notification.message}
            open={notification.open}
            onClose={() => setNotification((p) => ({ ...p, open: false }))}
          />
        </Stack>
      </FormWrapper>
    </CommonLayout>
  );
}

export default CodeVerifyPage;
